import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../i18n';
import styles from './LanguageSelector.module.css';

function LanguageSelector({ className = '' }) {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const selectorRef = useRef(null);

  const activeLanguage = useMemo(() => {
    const raw = i18n.language || 'en';
    const shortCode = raw.includes('-') ? raw.split('-')[0] : raw;
    const match = supportedLanguages.find((lang) => lang.value === shortCode);
    return match ? match.value : 'en';
  }, [i18n.language]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleMenu = () => setIsOpen((prev) => !prev);

  const handleSelect = (nextLang) => {
    i18n.changeLanguage(nextLang);
    setIsOpen(false);
  };

  return (
    <div ref={selectorRef} className={`${styles.selector} ${className}`.trim()}>
      <button
        type="button"
        className={`${styles.languageToggle} ${isOpen ? styles.languageToggleActive : ''}`.trim()}
        onClick={toggleMenu}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={t('language.label')}
      >
        <svg className={styles.languageIcon} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802" />
        </svg>

      </button>
      {isOpen && (
        <div className={styles.languageMenu} role="menu" aria-label={t('language.label')}>
          {supportedLanguages.map((lang) => (
            <button
              key={lang.value}
              type="button"
              className={`${styles.languageMenuItem} ${lang.value === activeLanguage ? styles.languageMenuItemActive : ''}`.trim()}
              onClick={() => handleSelect(lang.value)}
              role="menuitem"
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
